import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowRight,
  BookOpen,
  ExternalLink,
  FileText,
  GraduationCap,
  Image as ImageIcon,
  Layers3,
  Loader2,
  Search,
  ShieldCheck,
  Sparkles,
  Wrench,
} from 'lucide-react';
import { serviceInstructionsApi } from '../ai-platform/services/api';
import {
  TUTORIAL_MODULES,
  TUTORIAL_RESOURCES,
  findTutorialModule,
} from '../tutorial/tutorialContent';

const markdownComponents = {
  h2: ({ children }) => (
    <h2 className="mt-6 text-xl font-semibold text-textPrimary first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-5 text-base font-semibold text-textPrimary">{children}</h3>
  ),
  p: ({ children }) => <p className="text-sm leading-7 text-textSecondary">{children}</p>,
  ul: ({ children }) => (
    <ul className="my-3 list-disc space-y-2 pl-5 text-sm leading-7 text-textSecondary">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-3 list-decimal space-y-2 pl-5 text-sm leading-7 text-textSecondary">{children}</ol>
  ),
  strong: ({ children }) => <strong className="font-semibold text-textPrimary">{children}</strong>,
  code: ({ children }) => (
    <code className="rounded border border-borderDefault bg-backgroundInput px-1.5 py-0.5 text-xs text-textPrimary">
      {children}
    </code>
  ),
  a: ({ href, children }) => {
    const isExternal = href?.startsWith('http');
    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer' : undefined}
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {children}
      </a>
    );
  },
};

function ResourceLink({ resource }) {
  return (
    <a
      href={resource.href}
      target="_blank"
      rel="noreferrer"
      className="group rounded-lg border border-borderDefault bg-backgroundSurface p-4 transition hover:border-primary/50 hover:bg-primary/5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-borderDefault bg-backgroundInput p-2">
            <FileText className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-textPrimary group-hover:text-primary">{resource.label}</p>
            <p className="mt-1 text-xs leading-5 text-textSecondary">{resource.description}</p>
          </div>
        </div>
        <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-textSecondary group-hover:text-primary" />
      </div>
    </a>
  );
}

function ModuleButton({ module, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full rounded-lg border p-3 text-left transition',
        selected
          ? 'border-primary/60 bg-primary/10'
          : 'border-borderDefault bg-backgroundSurface hover:border-primary/40 hover:bg-primary/5',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
            {module.eyebrow}
          </p>
          <p className="mt-1 text-sm font-semibold text-textPrimary">{module.title}</p>
          <p className="mt-1 line-clamp-2 text-xs leading-5 text-textSecondary">{module.summary}</p>
        </div>
        <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-textSecondary" />
      </div>
    </button>
  );
}

function AppLinkButton({ link }) {
  return (
    <RouterLink
      to={link.href}
      className="inline-flex items-center justify-between gap-2 rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2 text-sm font-medium text-textPrimary transition hover:border-primary/50 hover:text-primary"
    >
      <span>{link.label}</span>
      <ArrowRight className="h-4 w-4" />
    </RouterLink>
  );
}

export default function TutorialPage() {
  const [selectedId, setSelectedId] = useState(TUTORIAL_MODULES[0].id);
  const [instruction, setInstruction] = useState(null);
  const [loadingInstruction, setLoadingInstruction] = useState(false);
  const [instructionError, setInstructionError] = useState('');

  const selectedModule = useMemo(() => findTutorialModule(selectedId), [selectedId]);

  useEffect(() => {
    let active = true;
    setLoadingInstruction(true);
    setInstruction(null);
    setInstructionError('');

    serviceInstructionsApi
      .getByKey(selectedModule.serviceKey)
      .then((data) => {
        if (!active) return;
        setInstruction(data);
      })
      .catch(() => {
        if (!active) return;
        setInstructionError(
          'Showing the packaged tutorial copy. Run backend migrations to expose the same module through service instructions and agent tools.'
        );
      })
      .finally(() => {
        if (!active) return;
        setLoadingInstruction(false);
      });

    return () => {
      active = false;
    };
  }, [selectedModule.serviceKey]);

  const backendImages = (instruction?.assets || [])
    .filter((asset) => asset.is_active !== false && asset.asset_type === 'image' && asset.file_url)
    .sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0))
    .map((asset) => ({
      src: asset.file_url,
      alt: asset.alt_text || asset.title,
      caption: asset.caption || asset.title,
    }));
  const displayImages = backendImages.length > 0 ? backendImages : selectedModule.images;
  const content = instruction?.content || [
    '## Lesson unavailable',
    '',
    'This lesson is served from Valpha service instructions. The packaged links and screenshots remain available, but the lesson body could not be loaded from the backend right now.',
  ].join('\n');

  return (
    <div data-tutorial-page className="mx-auto my-2 max-w-7xl space-y-4 sm:my-4 md:my-6">
      <section className="rounded-xl border border-borderDefault bg-backgroundSurface p-5 sm:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-primary">
              <GraduationCap className="h-5 w-5" />
              <p className="text-xs font-semibold uppercase tracking-[0.2em]">Tutorial</p>
            </div>
            <h1 className="mt-3 text-2xl font-bold text-textPrimary sm:text-3xl">
              Valpha AI systems, agents, and trading course
            </h1>
            <p className="mt-3 text-sm leading-7 text-textSecondary">
              Use this screen as the live course index. It combines the generated course PDFs, HTML tutorials,
              screenshots, app links, and service-instruction-backed content that agents can retrieve when helping users.
            </p>
          </div>

          <div className="grid min-w-0 grid-cols-2 gap-2 rounded-lg border border-borderDefault bg-backgroundInput p-3 text-xs text-textSecondary sm:min-w-[360px]">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <span>Course library</span>
            </div>
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-primary" />
              <span>Real screenshots</span>
            </div>
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-primary" />
              <span>Agent-ready content</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span>Trading safety</span>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        {TUTORIAL_RESOURCES.map((resource) => (
          <ResourceLink key={resource.href} resource={resource} />
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4">
            <div className="mb-3 flex items-center gap-2">
              <Layers3 className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold text-textPrimary">Course Modules</h2>
            </div>
            <div className="space-y-2">
              {TUTORIAL_MODULES.map((module) => (
                <ModuleButton
                  key={module.id}
                  module={module}
                  selected={module.id === selectedModule.id}
                  onClick={() => setSelectedId(module.id)}
                />
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold text-textPrimary">Reusable by Agents</h2>
            </div>
            <p className="mt-3 text-xs leading-6 text-textSecondary">
              This module is published with service key{' '}
              <code className="rounded border border-borderDefault bg-backgroundInput px-1.5 py-0.5 text-[11px] text-textPrimary">
                {selectedModule.serviceKey}
              </code>
              . Agents can discover it with{' '}
              <code className="rounded border border-borderDefault bg-backgroundInput px-1.5 py-0.5 text-[11px] text-textPrimary">
                list_service_instructions
              </code>{' '}
              and load the full lesson with{' '}
              <code className="rounded border border-borderDefault bg-backgroundInput px-1.5 py-0.5 text-[11px] text-textPrimary">
                get_service_instruction
              </code>
              .
            </p>
          </div>
        </aside>

        <main className="min-w-0 space-y-4">
          <article className="rounded-xl border border-borderDefault bg-backgroundSurface p-5 sm:p-6">
            <div className="flex flex-col gap-4 border-b border-borderDefault pb-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                  {selectedModule.eyebrow}
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-textPrimary">{selectedModule.title}</h2>
                <p className="mt-2 max-w-3xl text-sm leading-7 text-textSecondary">{selectedModule.summary}</p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full border border-borderDefault bg-backgroundInput px-3 py-1 text-textSecondary">
                  {selectedModule.level}
                </span>
                <span className="rounded-full border border-borderDefault bg-backgroundInput px-3 py-1 text-textSecondary">
                  {selectedModule.estimatedTime}
                </span>
                {loadingInstruction && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-borderDefault bg-backgroundInput px-3 py-1 text-textSecondary">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    service instruction
                  </span>
                )}
              </div>
            </div>

            {instructionError && (
              <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-6 text-amber-200">
                {instructionError}
              </div>
            )}

            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="min-w-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {content}
                </ReactMarkdown>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-textPrimary">What to master</h3>
                  </div>
                  <ul className="mt-3 space-y-2 text-xs leading-6 text-textSecondary">
                    {selectedModule.takeaways.map((takeaway) => (
                      <li key={takeaway}>{takeaway}</li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-4">
                  <h3 className="text-sm font-semibold text-textPrimary">Open in Valpha</h3>
                  <div className="mt-3 flex flex-col gap-2">
                    {selectedModule.appLinks.map((link) => (
                      <AppLinkButton key={link.href} link={link} />
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-4">
                  <h3 className="text-sm font-semibold text-textPrimary">Source lessons</h3>
                  <div className="mt-3 space-y-2">
                    {selectedModule.documentLinks.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between gap-2 rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2 text-sm text-textPrimary transition hover:border-primary/50 hover:text-primary"
                      >
                        <span>{link.label}</span>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>

          <section className="rounded-xl border border-borderDefault bg-backgroundSurface p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-textPrimary">Relevant Screens</h2>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {displayImages.map((image) => (
                <figure
                  key={image.src}
                  className="overflow-hidden rounded-lg border border-borderDefault bg-backgroundInput"
                >
                  <img src={image.src} alt={image.alt} className="h-auto w-full bg-backgroundSurface" loading="lazy" />
                  <figcaption className="border-t border-borderDefault p-3 text-xs leading-5 text-textSecondary">
                    {image.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        </main>
      </section>
    </div>
  );
}
